import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import {
  PrismButton,
  PrismButtonGroup,
  PrismCard,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import GeneralDropdownsDropdownYield from 'lumen/components/general/dropdowns/dropdown-yield';
import GeneralDropdownsTriggersTriggerDropdownIconButton from 'lumen/components/general/dropdowns/triggers/trigger-dropdown-icon-button';
import ManageColumns from 'lumen/components/market/snapshots/manage-columns';
import type {
  Kpi,
  MetricType,
} from 'lumen/models/market-performance/data-item';
import {
  GROUP_COLUMNS_BY,
  type GroupColumnsBy,
  isGroupColumnsBy,
  isSortOn,
  KPI_TRANSLATIONS,
  METRIC_TYPE_TRANSLATIONS,
  SORT_ON,
  type SortOn,
  type TableLayoutPreferences,
} from 'lumen/utils/constants/market-snapshots-constants';
import type { ColumnSettings } from 'lumen/utils/market-snapshots/column-settings';
import { localCopy } from 'tracked-toolbox';

export interface MarketSnapshotsTableLayoutSignature {
  Args: {
    /** For views that only manage categories. */
    hideGroupColumnsBy?: boolean;
    /** For views whose table can't be sorted. */
    hideSortOn?: boolean;
    manageColumns: {
      /** Pinned first column in categories mode (see `SnapshotViewColumnsConfig.fixed`). */
      fixed?: MetricType;
      kpiSettings: ColumnSettings<Kpi>;
      metrics: Record<string, MetricType>;
      settings: ColumnSettings;
    };
    onSave: (
      preferences: TableLayoutPreferences,
      columns: { kpiSettings: ColumnSettings<Kpi>; settings: ColumnSettings },
    ) => void;
    tableLayoutPreferences: TableLayoutPreferences;
  };
}

export default class MarketSnapshotsTableLayout extends Component<MarketSnapshotsTableLayoutSignature> {
  @service declare intl: Services['intl'];

  // Leaf paths, not `args.manageColumns`: `hash` rebuilds that object whenever
  // any value changes (e.g. metrics), which would discard an unsaved draft.
  @localCopy('args.manageColumns.settings')
  declare draftColumnSettings: ColumnSettings;

  @localCopy('args.manageColumns.kpiSettings')
  declare draftKpiColumnSettings: ColumnSettings<Kpi>;

  @localCopy('args.tableLayoutPreferences.groupColumnsBy')
  declare groupColumnsBy: GroupColumnsBy;

  @localCopy('args.tableLayoutPreferences.sortOn')
  declare sortOn: SortOn;

  get activeDraft(): ColumnSettings {
    return this.isKpiMode
      ? this.draftKpiColumnSettings
      : this.draftColumnSettings;
  }

  get columnLabels(): Record<string, string> {
    if (this.isKpiMode) {
      return Object.fromEntries(
        Object.entries(KPI_TRANSLATIONS).map(([kpi, key]) => [
          kpi,
          this.intl.t(key),
        ]),
      );
    }

    return Object.fromEntries(
      Object.entries(this.args.manageColumns.metrics).map(
        ([column, metric]) => [
          column,
          this.intl.t(METRIC_TYPE_TRANSLATIONS[metric]),
        ],
      ),
    );
  }

  // KPI sets never have a fixed column.
  get fixedLabel(): string | undefined {
    const { fixed } = this.args.manageColumns;

    return fixed && !this.isKpiMode
      ? this.intl.t(METRIC_TYPE_TRANSLATIONS[fixed])
      : undefined;
  }

  get isKpiMode(): boolean {
    return this.groupColumnsBy === GROUP_COLUMNS_BY.KPIS;
  }

  @action
  onCancel(dropdown?: { toggleDropdown: () => void }): void {
    this.draftColumnSettings = this.args.manageColumns.settings;
    this.draftKpiColumnSettings = this.args.manageColumns.kpiSettings;
    this.groupColumnsBy = this.args.tableLayoutPreferences.groupColumnsBy;
    this.sortOn = this.args.tableLayoutPreferences.sortOn;
    dropdown?.toggleDropdown();
  }

  @action
  onColumnSettingsChange(columnSettings: ColumnSettings): void {
    // ManageColumns is id-agnostic and hands back the settings it was given, so in
    // KPI mode the ids are Kpis; the cast restores what its `string` typing loses.
    if (this.isKpiMode) {
      this.draftKpiColumnSettings = columnSettings as ColumnSettings<Kpi>;
    } else {
      this.draftColumnSettings = columnSettings;
    }
  }

  @action
  onGroupColumnsByChange(groupColumnsBy: string | string[]): void {
    if (isGroupColumnsBy(groupColumnsBy)) {
      this.groupColumnsBy = groupColumnsBy;
    }
  }

  @action
  onSave(dropdown?: { toggleDropdown: () => void }): void {
    this.args.onSave(
      { groupColumnsBy: this.groupColumnsBy, sortOn: this.sortOn },
      {
        kpiSettings: this.draftKpiColumnSettings,
        settings: this.draftColumnSettings,
      },
    );
    dropdown?.toggleDropdown();
  }

  @action
  onSortOnChange(sortOn: string | string[]): void {
    if (isSortOn(sortOn)) {
      this.sortOn = sortOn;
    }
  }

  <template>
    <div class="relative">
      {{#let
        (component
          GeneralDropdownsTriggersTriggerDropdownIconButton
          buttonType="secondary"
          dataTestId="table-layout-trigger"
          iconBefore="settings"
        )
        as |triggerComponent|
      }}
        <GeneralDropdownsDropdownYield
          @dropdownClass=".dropdown-target"
          {{! @glint-expect-error: Incorrect type }}
          @dropdownClassName="dropdown-target w-[320px] top-[40px] right-0"
          @label={{t "market.snapshots.table_layout"}}
          @onCancel={{this.onCancel}}
          @selected={{null}}
          @triggerComponent={{triggerComponent}}
          as |dropdown|
        >
          <PrismCard>
            <:header as |Header|>
              <Header.title>
                {{t "market.snapshots.table_layout"}}
              </Header.title>
            </:header>
            <:content>
              <div class="grid gap-400">
                {{#unless @hideSortOn}}
                  <div class="flex items-center justify-between">
                    <span class="text-200--medium">
                      {{t "market.snapshots.sort_on"}}
                    </span>

                    <PrismButtonGroup
                      @onChange={{this.onSortOnChange}}
                      @value={{this.sortOn}}
                      aria-label={{t "market.snapshots.sort_on"}}
                      as |ButtonGroup|
                    >
                      <ButtonGroup.button
                        @label={{t "market.snapshots.sort_on.actuals"}}
                        @value={{SORT_ON.ACTUALS}}
                        data-test-id="sort-on-actuals"
                      />
                      <ButtonGroup.button
                        @label={{t "market.snapshots.sort_on.variance"}}
                        @value={{SORT_ON.VARIANCE}}
                        data-test-id="sort-on-variance"
                      />
                    </PrismButtonGroup>
                  </div>
                {{/unless}}

                {{#unless @hideGroupColumnsBy}}
                  <div class="flex items-center justify-between">
                    <span class="text-200--medium">
                      {{t "market.snapshots.group_columns_by"}}
                    </span>

                    <PrismButtonGroup
                      @onChange={{this.onGroupColumnsByChange}}
                      @value={{this.groupColumnsBy}}
                      aria-label={{t "market.snapshots.group_columns_by"}}
                      as |ButtonGroup|
                    >
                      <ButtonGroup.button
                        @label={{t "market.snapshots.group_columns.categories"}}
                        @value={{GROUP_COLUMNS_BY.CATEGORIES}}
                      />
                      <ButtonGroup.button
                        @label={{t "general.kpis"}}
                        @value={{GROUP_COLUMNS_BY.KPIS}}
                      />
                    </PrismButtonGroup>
                  </div>
                {{/unless}}

                <div class="grid gap-200">
                  <span class="text-200--medium">
                    {{t "market.snapshots.manage_columns"}}
                  </span>

                  <ManageColumns
                    @fixedLabel={{this.fixedLabel}}
                    @labels={{this.columnLabels}}
                    @onChange={{this.onColumnSettingsChange}}
                    @value={{this.activeDraft}}
                  />
                </div>
              </div>
            </:content>
            <:footer>
              <div class="flex flex-row justify-end gap-200">
                <PrismButton
                  @buttonType="secondary"
                  @label={{t "general.cancel"}}
                  {{on "click" (fn this.onCancel dropdown)}}
                />
                <PrismButton
                  @label={{t "general.apply"}}
                  {{on "click" (fn this.onSave dropdown)}}
                />
              </div>
            </:footer>
          </PrismCard>
        </GeneralDropdownsDropdownYield>
      {{/let}}
    </div>
  </template>
}
